/**
 * OKS-65 — Inertia prop shapes for the brand-side Crew-wide Analytics
 * surface (`/brand/analytics/crew`).
 *
 * Mirrors the server-side `BrandOps::CrewAnalytics::Result` Struct
 * shape 1:1 — keys are snake_case (Inertia JSON round-trip preserves
 * the Ruby serializer's keys).
 *
 * # State + split-KPI prop graph (TRD K8 / K11)
 *
 * The page branches on `state`:
 *   - `cold_start` → full-page empty CTA ("Invite creators"); all
 *                    data props nil.
 *   - `success`    → all data props populated; sub-empties are
 *                    FE-derived from prop shape (e.g.
 *                    `top_performers.length === 0`,
 *                    `engagement.total_oauth === 0`,
 *                    `engagement.no_snapshots_in_window`).
 *   - `error`      → analytics-error-state card with retry button.
 *
 * The KPI Hash is split into two so the window toggle's partial
 * reload preserves at-risk + 3 snapshot tiles per PRD §5 lock:
 *   - `kpis_snapshot` is window-INVARIANT (never reloads on toggle).
 *   - `kpis_window`   is window-SCOPED  (reloads with top_performers
 *                                        + engagement on toggle).
 *
 * # Partial-reload (TRD K12)
 *
 *   router.get(brandAnalyticsCrewPath({ window }), {}, {
 *     only: ['kpis_window', 'top_performers', 'engagement'],
 *     preserveScroll: true, preserveState: false
 *   })
 */

// =========================================================================
// Shared whitelists — mirror `BrandOps::CrewAnalyticsFilters` constants
// =========================================================================

import type { ReliabilityBand } from "./creator-analytics"

export const WINDOW_VALUES = ["30d", "90d", "all"] as const
export type CrewAnalyticsWindow = (typeof WINDOW_VALUES)[number]

// =========================================================================
// State discriminator (TRD K11 + controller error envelope)
// =========================================================================

export const CREW_ANALYTICS_STATES = ["cold_start", "success", "error"] as const
export type CrewAnalyticsState = (typeof CREW_ANALYTICS_STATES)[number]

// =========================================================================
// Reliability band discriminator — REUSED from OKS-64
// =========================================================================
//
// Top-performers rows carry only the three "qualifying" bands
// (`excellent` | `good` | `at_risk`). N/A members are excluded at
// the service layer (TRD K9.1). The widest type union exists in
// `creator-analytics.ts`; we narrow here for the table shape.

export type TopPerformerBand = Exclude<ReliabilityBand, "na">

// =========================================================================
// KPI split-Hash shape (TRD K8)
// =========================================================================

export interface CrewAnalyticsKpisSnapshot {
  /** Active CrewMembership count for the brand. Always Integer. */
  total_active: number
  /**
   * Active members with `social_account.status='active'` /
   * `total_active`. Float ∈ [0,1]. `null` only when `total_active`
   * is 0 (renders `—%`); `0.0` renders `0%`.
   */
  pct_oauth: number | null
  /**
   * `at_risk_scope(brand).count / total_active`. Float ∈ [0,1].
   * `null` only when `total_active` is 0. Fixed 21-day lookback
   * (PRD §3 lock — NOT window-driven).
   */
  pct_at_risk: number | null
}

export interface CrewAnalyticsKpisWindow {
  /**
   * Median reliability score across QUALIFYING members (band != "na").
   * Already rounded to Integer server-side. `null` when
   * `qualifying_count` is 0 (renders `—`).
   */
  median_reliability: number | null
  /** Count of members with non-N/A reliability in window. */
  qualifying_count: number
  /** Echoed for the FE caption ("in last {window}"). */
  window: CrewAnalyticsWindow
}

// =========================================================================
// Table row shapes (TRD K8.1)
// =========================================================================

export interface AtRiskRow {
  crew_membership_id: number
  user_id: number
  tiktok_username: string | null
  avatar_url: string | null
  /**
   * Whole-days since last brand-submission. Capped at 1000 for
   * lifetime-zero-submission members (FE renders "1000+ days").
   */
  days_since_last_submission: number
  lifetime_collabs: number
}

export interface TopPerformerRow {
  crew_membership_id: number
  user_id: number
  tiktok_username: string | null
  avatar_url: string | null
  /** Already rounded server-side. */
  reliability_score: number
  reliability_band: TopPerformerBand
  in_window_submissions: number
}

// =========================================================================
// Engagement distribution shape (TRD K8.2 — self-describing buckets)
// =========================================================================

export const ENGAGEMENT_BUCKET_LABELS = [
  "starting",
  "developing",
  "engaged",
  "high",
  "viral",
] as const
export type EngagementBucketLabel = (typeof ENGAGEMENT_BUCKET_LABELS)[number]

export interface EngagementBucket {
  label: EngagementBucketLabel
  /** Inclusive lower bound (likes/video). */
  min: number
  /**
   * Inclusive upper bound (likes/video). `null` on the open-ended
   * `viral` bucket (FE branches on `max === null` for the "100k+"
   * label).
   */
  max: number | null
  /** Cohort count in this bucket (always present, can be 0). */
  count: number
}

export interface CrewEngagement {
  /** 5 rows, ordered, always present. */
  buckets: EngagementBucket[]
  /**
   * Active members without OAuth (or status != active). Footer
   * caption: "{excluded_count} of {total_active} haven't connected
   * TikTok".
   */
  excluded_count: number
  /** Active members with OAuth + at least one in-window snapshot. */
  total_oauth: number
  /** Self-contained denominator for the footer caption. */
  total_active: number
  /**
   * OAuth members exist but have zero snapshots in this window.
   * UX Spec §3 → empty-card "No engagement data in this window — try
   * All-time" with an inline "View all-time" button.
   */
  no_snapshots_in_window: boolean
}

// =========================================================================
// Top-level page props
// =========================================================================

export interface CrewAnalyticsProps {
  state: CrewAnalyticsState
  /** URL query echo — drives the toggle's active state. */
  window: CrewAnalyticsWindow
  /** Window-invariant KPIs. `null` on `cold_start` / `error`. */
  kpis_snapshot: CrewAnalyticsKpisSnapshot | null
  /** Window-scoped KPIs. `null` on `cold_start` / `error`. */
  kpis_window: CrewAnalyticsKpisWindow | null
  /** Top-5 at-risk rows. Empty array = "no at-risk" empty card. `null` on `cold_start` / `error`. */
  at_risk: AtRiskRow[] | null
  /** Top-5 performer rows. Empty array = "all N/A" empty card. `null` on `cold_start` / `error`. */
  top_performers: TopPerformerRow[] | null
  /** Engagement card payload. `null` on `cold_start` / `error`. */
  engagement: CrewEngagement | null
}
